import React from 'react';
import {
  Actions,
  BackButton,
  BackIcon,
  Header,
  IconButton,
  IconClose,
  IconGlobe,
  IconShare,
} from './MenuVoteHeader.styles';

export type MenuVoteHeaderVariant = 'default' | 'white';

export interface MenuVoteHeaderProps {
  variant?: MenuVoteHeaderVariant;
  onBack?: () => void;
  onGlobe?: () => void;
  onShare?: () => void;
  onClose?: () => void;
}

/** Figma 135:5955 — icon/back-button 28×28 (투표: 검정 원 + 흰 chevron) */
function BackIconDefault() {
  return (
    <BackIcon viewBox="0 0 28 28" width={28} height={28} aria-hidden="true" focusable="false">
      <rect width="28" height="28" rx="14" fill="#000000" />
      <path d="M8 11L14 17L20 11" stroke="#FFFFFF" strokeWidth="1.5" fill="none" />
    </BackIcon>
  );
}

/** Figma 135:6295 — icon/back-button (결과: 흰 chevron만, 원형 배경 없음) */
function BackIconWhiteGraphic() {
  return (
    <BackIcon viewBox="0 0 28 28" width={28} height={28} aria-hidden="true" focusable="false">
      <path
        d="M6 10L14 18L22 10"
        stroke="#FFFFFF"
        strokeWidth="1.5"
        fill="none"
        transform="rotate(90 14 14)"
      />
    </BackIcon>
  );
}

/** Figma 135:5955 / 135:6295 — icon/globe 19×19 */
function GlobeIcon() {
  return (
    <IconGlobe viewBox="0 0 17.0568 17.0568" width={19} height={19} aria-hidden="true" focusable="false">
      <path
        d="M8.5284 16.5068C12.9347 16.5068 16.5068 12.9347 16.5068 8.5284C16.5068 4.12205 12.9347 0.55 8.5284 0.55C4.12205 0.55 0.55 4.12205 0.55 8.5284C0.55 12.9347 4.12205 16.5068 8.5284 16.5068Z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M8.5284 0.55V16.4726" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path
        d="M4.63754 8.51129C4.63754 14.2749 8.51984 16.4726 8.51984 16.4726C8.51984 16.4726 12.4022 14.2749 12.4022 8.51129C12.4022 2.74769 8.51984 0.55 8.51984 0.55C8.51984 0.55 4.63754 2.74769 4.63754 8.51129Z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M0.55 8.5284H16.4982" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </IconGlobe>
  );
}

/** Figma 135:5955 / 135:6295 — icon/share 16×17 */
function ShareIcon() {
  return (
    <IconShare viewBox="0 0 16.1226 17.1178" width={16} height={17} aria-hidden="true" focusable="false">
      <path
        d="M15.5226 8.27481V16.5178H8.06128H0.6V8.27481"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="square"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M11.9939 4.29763L8.06128 0.600014V11.0255V0.600014L4.12862 4.29763"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="square"
        strokeLinejoin="round"
        fill="none"
      />
    </IconShare>
  );
}

/** Figma 135:5955 — icon/close 15×15 (투표 화면만) */
function CloseIcon() {
  return (
    <IconClose viewBox="0 0 15.1661 15.1661" width={15} height={15} aria-hidden="true" focusable="false">
      <path d="M0.6 14.5661L14.5661 0.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M14.5661 14.5661L0.6 0.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </IconClose>
  );
}

/**
 * 메뉴 투표 헤더 (widget)
 * - default (135:5955): 검정 원 back + globe/share/close (검정 stroke)
 * - white (135:6295): 흰 chevron back + globe/share (흰 stroke, close 없음)
 */
export function MenuVoteHeader({
  variant = 'default',
  onBack,
  onGlobe,
  onShare,
  onClose,
}: MenuVoteHeaderProps) {
  const isWhite = variant === 'white';

  return (
    <Header>
      <BackButton type="button" onClick={onBack} aria-label="뒤로 가기">
        {isWhite ? <BackIconWhiteGraphic /> : <BackIconDefault />}
      </BackButton>

      <Actions>
        <IconButton type="button" $white={isWhite} onClick={onGlobe} aria-label="언어">
          <GlobeIcon />
        </IconButton>

        <IconButton type="button" $white={isWhite} onClick={onShare} aria-label="공유">
          <ShareIcon />
        </IconButton>
      </Actions>
    </Header>
  );
}
